<template>
  <v-card flat class="pa-4">
    <v-card-title>
      <v-icon left color="primary">mdi-cog</v-icon>
      <span class="headline">App Settings</span>
    </v-card-title>
    <v-divider class="mb-4" />

    <!-- Inactivity Logout Settings -->
    <v-card flat class="mb-4 pa-4" outlined>
      <v-card-title class="subtitle-1 font-weight-bold">
        <v-icon left color="warning">mdi-timer-off</v-icon>
        Inactivity Logout
      </v-card-title>
      <v-card-text>
        <v-switch
          v-model="settings.inactivityEnabled"
          label="Enable Inactivity Auto-Logout"
          color="primary"
        />
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model.number="settings.inactivityLimit"
              :disabled="!settings.inactivityEnabled"
              label="Logout after inactivity (seconds)"
              type="number"
              min="10"
              max="3600"
              hint="How long before user is logged out (min: 10s, max: 1hr)"
              persistent-hint
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model.number="settings.warningDelayLimit"
              :disabled="!settings.inactivityEnabled"
              label="Show warning before logout (seconds)"
              type="number"
              min="5"
              :max="settings.inactivityLimit - 1"
              hint="How many seconds before logout to show warning"
              persistent-hint
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- SMTP Provider Settings -->
    <SmtpSettings v-model="settings.smtp" />

    <!-- Suggestions for Future Settings -->
    <v-card flat class="mb-4 pa-4" outlined>
      <v-card-title class="subtitle-1 font-weight-bold">
        <v-icon left color="primary">mdi-lightbulb-on-outline</v-icon>
        Coming Soon
      </v-card-title>
      <v-list dense>
        <v-list-item>
          <v-list-item-icon><v-icon>mdi-palette</v-icon></v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Theme (Light/Dark/Auto)</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item>
          <v-list-item-icon><v-icon>mdi-bell-ring</v-icon></v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Notification Preferences</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item>
          <v-list-item-icon><v-icon>mdi-history</v-icon></v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Audit Log Retention Period</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item>
          <v-list-item-icon><v-icon>mdi-file-upload</v-icon></v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>File Upload Size Limit</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item>
          <v-list-item-icon><v-icon>mdi-account-key</v-icon></v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Password Policy</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card>

    <v-card-actions>
      <v-spacer />
      <v-btn
        color="primary"
        @click="saveSettings"
        :disabled="!canSave || !hasChanges || saving"
      >
        Save Settings
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import axios from "axios";
import { apiConfig } from "@/config/api.config";
import SmtpSettings from "./SmtpSettings.vue";

export default {
  name: "ManagementSettings",
  components: { SmtpSettings },
  data() {
    return {
      settings: {
        inactivityEnabled: true,
        inactivityLimit: 30, // seconds
        warningDelayLimit: 20, // seconds
        smtp: {
          smtpProvider: "gmail",
          smtpConfig: { user: "", pass: "" },
        },
      },
      originalSettings: null,
      saving: false,
      loading: false,
      error: null,
    };
  },
  computed: {
    canSave() {
      return (
        this.settings.inactivityLimit > 10 &&
        this.settings.warningDelayLimit >= 5 &&
        this.settings.warningDelayLimit < this.settings.inactivityLimit
      );
    },
    hasChanges() {
      return (
        !this.originalSettings ||
        JSON.stringify(this.settings) !== JSON.stringify(this.originalSettings)
      );
    },
  },
  methods: {
    async saveSettings() {
      this.saving = true;
      this.error = null;
      try {
        await axios.put(
          `${apiConfig.baseURL}/settings/management`,
          this.settings
        );
        this.originalSettings = JSON.parse(JSON.stringify(this.settings));
        this.$emit("settings-saved", { ...this.settings });
        // Update Vuex store after save
        this.$store.dispatch("settings/fetchSettings");
        this.$store.dispatch("setSnackbar", {
          showing: true,
          color: "success",
          text: "Settings saved!",
        });
      } catch (e) {
        this.error = "Failed to save settings.";
      } finally {
        this.saving = false;
      }
    },
    async loadSettings() {
      this.loading = true;
      this.error = null;
      try {
        const res = await axios.get(`${apiConfig.baseURL}/settings/management`);
        if (res.data) {
          // Deep merge to preserve Vue reactivity
          if (res.data.smtp) {
            if (!this.settings.smtp) this.$set(this.settings, "smtp", {});
            Object.keys(res.data.smtp).forEach((key) => {
              this.$set(this.settings.smtp, key, res.data.smtp[key]);
            });
            // Deep merge smtpConfig if present
            if (res.data.smtp.smtpConfig) {
              if (!this.settings.smtp.smtpConfig)
                this.$set(this.settings.smtp, "smtpConfig", {});
              Object.keys(res.data.smtp.smtpConfig).forEach((key) => {
                this.$set(
                  this.settings.smtp.smtpConfig,
                  key,
                  res.data.smtp.smtpConfig[key]
                );
              });
            }
          }
          // Merge top-level settings (excluding smtp, already handled)
          Object.keys(res.data).forEach((key) => {
            if (key !== "smtp") this.settings[key] = res.data[key];
          });
        }
        this.originalSettings = JSON.parse(JSON.stringify(this.settings));
      } catch (e) {
        this.error = "Failed to load settings.";
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.loadSettings();
  },
};
</script>

<style scoped>
.headline {
  font-weight: 600;
  font-size: 1.3em;
}
</style>
